import { useCustomerAuth } from '@agos/customer-core'
import { retryTransient } from '@agos/api-client'
import { useGetCustomerAuthPersonalizedSuggestion } from '@agos/api-client/customers'
import { T, formatCents, tt } from '@agos/shared'
import { Check, Sparkles, Tag, UtensilsCrossed, X } from 'lucide-react'
import { useState } from 'react'

import {
  couponNote,
  discountAction,
  discountGreeting,
  greetingOf,
  varietyGreeting,
  webMenuProduct,
  type DiscountAction,
  type GreetingDiscountCard,
  type MenuProduct,
} from './greeting'

/**
 * The two cards the home page shows somebody it recognises.
 *
 * `HomePage.tsx` passed `greetingName={null}` as a literal, so this section rendered for **nobody** —
 * a franchise switching it on got nothing, and the rules and templates behind it were dead code. That
 * is the eighth time in this port that something was written, tested and connected to nothing.
 *
 * Both cards are dismissable and neither returns after being dismissed within the visit, as legacy has
 * it: a suggestion that cannot be waved away is an advertisement.
 *
 * The discount card offers to add the dish **only when it is still on the menu at a price**. The server
 * sends `price: null` for one that has been withdrawn since the order that earned the suggestion, and
 * adding it at a price nobody set is FB-C29's mistake in a different jacket.
 *
 * **FRONTEND.md §9.776, owner O137** - *"dajú sa objednať veci, ktoré tam nie sú, respektíve sú iba v
 * manageri"*. The card called the basket's `add` itself, with the server's id: no check that the menu
 * offers the dish, no opening-hours gate, no *Momentálne nedostupné*, no add-on question and no size.
 * Each card is now checked against the menu the customer is looking at ({@link webMenuProduct}) and is
 * not drawn when the menu does not offer its dish - nor while the menu has not loaded, rather than
 * guessing - and the add goes through `onAdd`, the menu's own add the home tiles use.
 */

const CARD = 'rounded-2xl border p-4 flex items-start gap-3'

export function PersonalizedGreeting(props: {
  /** The menu's products, as `GET /products` answers them - hidden ones included. */
  products: readonly MenuProduct[]
  /** The categories the menu draws, already filtered on `visibleOnWeb`. */
  menuCategoryIds: ReadonlySet<number>
  /** The opening-hours gate the home tiles honour. */
  mayOrder: boolean
  /** The menu's own add - the add-on question included. */
  onAdd: (productId: number) => void
  onOpenCategory?: (categoryId: number | null) => void
}) {
  const signedIn = useCustomerAuth((state) => state.user) !== null

  /* FB-835, §9.549 - **this card holds a coupon code, so a failed read is an offer nobody sees.** FB-831's shape:
     what the basket never offered cannot be recovered downstream. And §10A.245 found seventeen `error` rows in one hour
     on this very endpoint in the owner's own `system_logs` - that cause is fixed, and it is what says the failures here
     were real rather than theoretical. `retryTransient` keeps a 401 final, which `enabled` already covers. */
  const { data } = useGetCustomerAuthPersonalizedSuggestion({
    query: { enabled: signedIn, retry: retryTransient, staleTime: 300_000 },
  })

  const [dismissed, setDismissed] = useState<{ discount: boolean; variety: boolean }>({
    discount: false,
    variety: false,
  })
  const [added, setAdded] = useState(false)
  const [copied, setCopied] = useState(false)

  const greeting = greetingOf(data)
  if (!signedIn || greeting === null) return null

  const now = new Date()
  const discountProduct =
    greeting.discount === null
      ? null
      : webMenuProduct(greeting.discount.productId, props.products, props.menuCategoryIds)
  const varietyProduct =
    greeting.variety === null
      ? null
      : webMenuProduct(greeting.variety.productId, props.products, props.menuCategoryIds)
  const action: DiscountAction | null =
    greeting.discount === null || discountProduct === null
      ? null
      : discountAction(greeting.discount, discountProduct, props.mayOrder)
  const showDiscount = action !== null && !dismissed.discount
  const showVariety = varietyProduct !== null && !dismissed.variety
  if (!showDiscount && !showVariety) return null

  function pressDiscount(card: GreetingDiscountCard, chosen: DiscountAction) {
    if (chosen === 'add') {
      props.onAdd(card.productId)
      setAdded(true)
      return
    }
    if (chosen === 'look' || chosen === 'choose-size') props.onOpenCategory?.(card.categoryId)
  }

  return (
    <div className="space-y-3" data-greeting>
      {showDiscount && greeting.discount !== null && (
        <div className={`${CARD} bg-primary/5 border-primary/20`} data-greeting-discount>
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-sm font-semibold" data-greeting-line>
              {discountGreeting(greeting, now)}
            </p>
            {greeting.discount.coupon !== null && (
              <button
                type="button"
                data-greeting-coupon={greeting.discount.coupon.code}
                onClick={() => {
                  // `writeText` can be refused — an insecure origin, a denied permission — and a card
                  // that silently does nothing is worse than one that never offered.
                  void navigator.clipboard
                    ?.writeText(greeting.discount?.coupon?.code ?? '')
                    .then(() => setCopied(true))
                    .catch(() => setCopied(false))
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary"
              >
                <Tag className="w-3.5 h-3.5" />
                {greeting.discount.coupon.code}
                {couponNote(greeting.discount.coupon, formatCents) !== '' && (
                  <span className="opacity-70">
                    · {couponNote(greeting.discount.coupon, formatCents)}
                  </span>
                )}
                {copied && <Check className="w-3.5 h-3.5" />}
              </button>
            )}
            {action !== null && (
              <button
                type="button"
                data-greeting-add={greeting.discount.productId}
                data-greeting-action={action}
                disabled={action === 'unavailable' || action === 'closed'}
                onClick={() => greeting.discount !== null && pressDiscount(greeting.discount, action)}
                className="block min-h-[36px] rounded-xl bg-primary px-3 text-xs font-bold text-primary-foreground disabled:opacity-50"
              >
                {action === 'look' ? (
                  <T>Pozrieť</T>
                ) : action === 'choose-size' ? (
                  <T>Vybrať veľkosť</T>
                ) : action === 'unavailable' ? (
                  <T>Momentálne nedostupné</T>
                ) : added ? (
                  <T>Pridané!</T>
                ) : (
                  <T>Pridať do košíka</T>
                )}
              </button>
            )}
          </div>
          <button
            type="button"
            data-greeting-dismiss="discount"
            aria-label={tt('Zavrieť')}
            onClick={() => setDismissed({ ...dismissed, discount: true })}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showVariety && greeting.variety !== null && (
        <div className={`${CARD} bg-card border-border`} data-greeting-variety>
          <div className="w-10 h-10 rounded-xl bg-green-600/10 text-green-600 flex items-center justify-center shrink-0">
            <UtensilsCrossed className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-sm font-semibold" data-greeting-line>
              {varietyGreeting(greeting, now)}
            </p>
            <button
              type="button"
              data-greeting-look={greeting.variety.productId}
              onClick={() => props.onOpenCategory?.(greeting.variety?.categoryId ?? null)}
              className="block min-h-[36px] rounded-xl border border-border px-3 text-xs font-semibold"
            >
              <T>Pozrieť</T>
            </button>
          </div>
          <button
            type="button"
            data-greeting-dismiss="variety"
            aria-label={tt('Zavrieť')}
            onClick={() => setDismissed({ ...dismissed, variety: true })}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
